<template>
  <Teleport to="body">
    <Transition name="dialog">
      <div
        v-if="isOpen"
        class="command-palette-overlay"
        @click="closeDialog"
        role="presentation">
        <div
          role="dialog"
          class="command-palette-dialog"
          :aria-describedby="descriptionId"
          :aria-labelledby="titleId"
          @click.stop
          tabindex="-1">

          <div class="dialog-content">
            <!-- Screen Reader Only Title & Description -->
            <span class="sr-only">
              <h2 :id="titleId" class="dialog-title">
                {{ title }}
              </h2>
            </span>
            <p :id="descriptionId" class="dialog-description sr-only">
              {{ description }}
            </p>

            <!-- Main Content -->
            <div class="dialog-body">
              <!-- Search Header -->
              <div class="search-header">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="search-icon"
                  aria-hidden="true">
                  <path d="m21 21-4.34-4.34"></path>
                  <circle cx="11" cy="11" r="8"></circle>
                </svg>

                <input
                  ref="searchInput"
                  v-model="searchQuery"
                  type="text"
                  class="search-input"
                  placeholder="Suche..."
                  @keydown.down.prevent="selectNext"
                  @keydown.up.prevent="selectPrevious"
                  @keydown.enter.prevent="navigateToSelected"
                  @keydown.esc="closeDialog"
                />

                <div class="keyboard-shortcuts">
                  <kbd class="kbd">⌘</kbd>
                  <span>+</span>
                  <kbd class="kbd">K</kbd>
                </div>
              </div>

              <!-- Results List -->
              <div class="results-container">
                <div class="results-list">
                  <div
                    v-for="(item, index) in filteredItems"
                    :key="item.id"
                    :ref="el => { if (el) itemRefs[index] = el }"
                    class="result-item"
                    :class="{
                      'active': selectedIndex === index,
                      'has-badge': item.isRecent
                    }"
                    @click="navigateTo(item)"
                    @mouseenter="selectedIndex = index"
                    role="button"
                    :aria-selected="selectedIndex === index">

                    <!-- Icon -->
                    <div class="item-icon" :class="`icon-${item.category}`">
                      <component :is="item.icon" class="icon" aria-hidden="true" />
                    </div>

                    <!-- Content -->
                    <div class="item-content">
                      <div class="item-header">
                        <span class="item-title">{{ item.title }}</span>
                        <span
                          v-if="item.isRecent"
                          class="recent-badge">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="clock-icon"
                            aria-hidden="true">
                            <path d="M12 6v6l4 2"></path>
                            <circle cx="12" cy="12" r="10"></circle>
                          </svg>
                          Kürzlich
                        </span>
                      </div>
                      <p class="item-description">{{ item.description }}</p>
                    </div>

                    <!-- Tags & Arrow -->
                    <div class="item-actions">
                      <span class="item-tag">{{ item.tag }}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="arrow-icon"
                        aria-hidden="true">
                        <path d="M5 12h14"></path>
                        <path d="m12 5 7 7-7 7"></path>
                      </svg>
                    </div>
                  </div>

                  <!-- No Results -->
                  <div v-if="filteredItems.length === 0" class="no-results">
                    <p>Keine Ergebnisse gefunden</p>
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="dialog-footer">
                <div class="footer-shortcuts">
                  <div class="shortcut-group">
                    <kbd class="kbd">↑↓</kbd>
                    <span>navigieren</span>
                  </div>
                  <div class="shortcut-group">
                    <kbd class="kbd">↵</kbd>
                    <span>auswählen</span>
                  </div>
                  <div class="shortcut-group">
                    <kbd class="kbd">esc</kbd>
                    <span>schließen</span>
                  </div>
                </div>
                <div class="footer-brand">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="command-icon"
                    aria-hidden="true">
                    <path d="M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3"></path>
                  </svg>
                  <span>BMW</span>
                </div>
              </div>
            </div>

            <!-- Close Button -->
            <button
              type="button"
              class="close-button"
              @click="closeDialog"
              aria-label="Dialog schließen">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
              <span class="sr-only">Dialog schließen</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, computed, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import {
  ChartColumn,
  Zap,
  MessageSquare,
  Users,
  Target,
  Database
} from 'lucide-vue-next';

export default {
  name: 'CommandPalette',

  components: {
    ChartColumn,
    Zap,
    MessageSquare,
    Users,
    Target,
    Database
  },

  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const router = useRouter();
    const searchInput = ref(null);
    const searchQuery = ref('');
    const selectedIndex = ref(0);
    const itemRefs = ref([]);

    const titleId = 'command-palette-title';
    const descriptionId = 'command-palette-description';
    const title = 'BMW Command Palette';
    const description = 'Schnelle Navigation zwischen Modulen. Verwenden Sie die Pfeiltasten zum Navigieren und Enter zum Auswählen.';

    const items = ref([
      {
        id: 'dashboard',
        title: 'Dashboard Übersicht',
        description: 'Übersicht der BMW Dashboard Module',
        route: '/dashboard',
        icon: ChartColumn,
        tag: 'Dashboard',
        category: 'primary',
        isRecent: true
      },
      {
        id: 'tasks',
        title: 'Aufgaben-Manager',
        description: 'Durchgängiges Request-to-PO-Management mit Workflow-Engine',
        route: '/tasks',
        icon: Zap,
        tag: 'BMW',
        category: 'secondary',
        isRecent: true
      },
      {
        id: 'assistant',
        title: 'Persönlicher Assistent',
        description: 'KI-gestützte kontextsensitive Unterstützung',
        route: '/assistant',
        icon: MessageSquare,
        tag: 'BMW',
        category: 'secondary',
        isRecent: true
      },
      {
        id: 'locations',
        title: 'Standort-Manager',
        description: 'Self-Service-Portal mit automatischer Datenvalidierung',
        route: '/locations',
        icon: Users,
        tag: 'BMW',
        category: 'secondary',
        isRecent: false
      },
      {
        id: 'quality',
        title: 'Qualitäts-Center',
        description: 'Mystery Shopping Management und Qualitätssicherung',
        route: '/quality',
        icon: Target,
        tag: 'BMW',
        category: 'secondary',
        isRecent: false
      },
      {
        id: 'reporting',
        title: 'Reporting Cockpit',
        description: 'Live-Dashboards mit AI-Insights und interaktiven Ansichten',
        route: '/reporting',
        icon: Database,
        tag: 'BMW',
        category: 'secondary',
        isRecent: false
      }
    ]);

    const isOpen = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    });

    const filteredItems = computed(() => {
      if (!searchQuery.value) {
        return items.value;
      }

      const query = searchQuery.value.toLowerCase();
      return items.value.filter(item =>
        item.title.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query)
      );
    });

    const selectNext = () => {
      selectedIndex.value = (selectedIndex.value + 1) % filteredItems.value.length;
      scrollToSelected();
    };

    const selectPrevious = () => {
      selectedIndex.value = selectedIndex.value === 0
        ? filteredItems.value.length - 1
        : selectedIndex.value - 1;
      scrollToSelected();
    };

    const scrollToSelected = () => {
      nextTick(() => {
        const selectedElement = itemRefs.value[selectedIndex.value];
        if (selectedElement) {
          selectedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          });
        }
      });
    };

    const navigateTo = (item) => {
      router.push(item.route);
      closeDialog();
    };

    const navigateToSelected = () => {
      if (filteredItems.value[selectedIndex.value]) {
        navigateTo(filteredItems.value[selectedIndex.value]);
      }
    };

    const closeDialog = () => {
      isOpen.value = false;
    };

    // Focus search input when dialog opens
    watch(isOpen, (newValue) => {
      if (newValue) {
        nextTick(() => {
          searchInput.value?.focus();
        });
        selectedIndex.value = 0;
        searchQuery.value = '';
      }
    });

    // Close on Escape key globally
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen.value) {
        closeDialog();
      }
    };

    // Add global keyboard listener
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handleEscape);
    }

    return {
      searchInput,
      searchQuery,
      selectedIndex,
      itemRefs,
      titleId,
      descriptionId,
      title,
      description,
      isOpen,
      filteredItems,
      selectNext,
      selectPrevious,
      navigateTo,
      navigateToSelected,
      closeDialog
    };
  }
}
</script>

<style lang="scss" scoped>
// Overlay
.command-palette-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 64px 16px;
  overflow-y: auto;
}

// Dialog
.command-palette-dialog {
  position: relative;
  width: 100%;
  max-width: 672px;
  background-color: var(--color-background);
  backdrop-filter: blur(40px);
  border-radius: var(--radius-lg);
  overflow: hidden;
  max-height: 80vh;
  display: flex;
  flex-direction: column;

  @media (min-width: 640px) {
    max-width: 900px;
  }

  @media (min-width: 768px) {
    max-width: 1000px;
  }

  @media (min-width: 992px) {
    max-width: 1120px;
  }
}

.dialog-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  height: 100%;
}

// Screen Reader Only
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.dialog-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--color-foreground);
  line-height: 1.3;
}

.dialog-description {
  color: var(--color-muted-foreground);
  font-size: 16px;
  line-height: 1.5;
}

// Search Header
.search-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid rgba(229, 231, 235, 0.5);
  flex-shrink: 0;

  .search-icon {
    width: 20px;
    height: 20px;
    color: #9ca3af;
    flex-shrink: 0;
  }

  .search-input {
    flex: 1;
    min-width: 0;
    border: 0;
    background: transparent;
    font-size: 18px;
    color: var(--color-foreground);
    outline: none;
    padding: 0;
    height: auto;

    &::placeholder {
      color: #9ca3af;
    }
  }

  .keyboard-shortcuts {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #9ca3af;
    flex-shrink: 0;

    span {
      font-size: 10px;
    }
  }
}

// Results Container
.results-container {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.results-list {
  padding: 8px;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 4px;

  &:hover,
  &.active {
    background-color: #f3f4f6;
  }

  &.active {
    background-color: #eff6ff;
    border: 1px solid #bfdbfe;
  }
}

.item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  flex-shrink: 0;

  .icon {
    width: 16px;
    height: 16px;
  }

  &.icon-primary {
    background-color: #dbeafe;
    color: #2563eb;
  }

  &.icon-secondary {
    background-color: #f3f4f6;
    color: var(--color-muted-foreground);
  }
}

.item-content {
  flex: 1;
  min-width: 0;
}

.item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.item-title {
  font-weight: 500;
  color: var(--color-foreground);
  font-size: 14px;
  word-break: break-word;
}

.recent-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  background-color: transparent;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xs);
  font-size: 12px;
  color: var(--color-muted-foreground);
  flex-shrink: 0;

  .clock-icon {
    width: 12px;
    height: 12px;
  }
}

.item-description {
  font-size: 14px;
  color: var(--color-muted-foreground);
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.item-tag {
  padding: 4px 8px;
  background-color: #dbeafe;
  color: #1e40af;
  font-size: 12px;
  font-weight: 500;
  border-radius: var(--radius-xs);
  white-space: nowrap;
}

.arrow-icon {
  width: 16px;
  height: 16px;
  color: #9ca3af;
}

.no-results {
  padding: 48px 24px;
  text-align: center;

  p {
    color: var(--color-muted-foreground);
    font-size: 14px;
    margin: 0;
  }
}

// Footer
.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-top: 1px solid rgba(229, 231, 235, 0.5);
  background-color: var(--color-background);
  flex-shrink: 0;
}

.footer-shortcuts {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.shortcut-group {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--color-muted-foreground);
  white-space: nowrap;
}

.kbd {
  padding: 4px 6px;
  color: var(--color-foreground);
  background-color: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xs);
  font-size: 12px;
  font-family: monospace;
  line-height: 1;
}

.footer-brand {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #9ca3af;
  flex-shrink: 0;
  margin-left: 16px;

  .command-icon {
    width: 12px;
    height: 12px;
  }

  span {
    white-space: nowrap;
  }
}

// Close Button
.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  padding: 8px;
  opacity: 0.7;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    opacity: 1;
    background-color: #f3f4f6;
  }

  &:focus-visible {
    outline: 2px solid #f50537;
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
}

// Transitions
.dialog-enter-active,
.dialog-leave-active {
  transition: opacity 0.2s ease;

  .command-palette-dialog {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
}

.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;

  .command-palette-dialog {
    opacity: 0;
    transform: translateY(-8px) scale(0.95);
  }
}

.dialog-enter-to,
.dialog-leave-from {
  opacity: 1;

  .command-palette-dialog {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
